/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { GalleryTile } from "./GalleryTile";
import { getTileDataForMode } from './data/galleryUtils';
import { GalleryMode } from './data/galleryTypes';
import { useTheme } from '../Theme/ThemeProvider';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

interface MobileGalleryProps {
  current: { mode: GalleryMode | null; direction: number };
  onTileClick?: (prompt: string, tool?: string) => void;
}

/**
 * Mobile/Tablet Gallery Component
 * Displays tiles in a horizontal scrollable row
 * Shows 2 full tiles + 1 partial tile at a time
 */
export function MobileGallery({ current, onTileClick }: MobileGalleryProps) {
  const { mode } = useTheme();
  const { isTablet } = useDeviceDetection();

  if (!current.mode) return null;

  const tileData = getTileDataForMode(current.mode);
  const tiles = tileData.try; // Get the 'try' array which contains the tiles

  return (
    <div className="w-full" style={{ height: isTablet ? '120px' : '80px' }}>
      {/* Horizontal Scrollable Container */}
      <div 
        className="overflow-x-auto overflow-y-hidden scrollbar-hide h-full pb-2"
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
        }}
      >
        {/* Tiles Container */}
        <div 
          className="flex h-full"
          style={{
            // Tablet: pl=32px (matches ChatInput md:px-8), pr=16px. Mobile: px=16px (matches ChatInput px-4).
            paddingLeft: isTablet ? '32px' : '16px',
            paddingRight: '16px',
            // Gap between tiles: 10px
            gap: '10px',
            width: 'max-content',
          }}
        >
          {tiles.map((tile, i) => {
            // Type assertion - we know tiles have prompt property from galleryData
            const tileWithPrompt = tile as { title: string; subtitle: string; phoneSubtitle?: string; prompt: string; tool?: string };
            // Tablet: pl(32) + pr(16) + gaps(10) = 58px. Mobile: pl(16) + pr(16) + gaps(10) = 42px.
            const totalOffset = isTablet ? '58px' : '42px';
            return (
              <GalleryTile
                key={i}
                title={tileWithPrompt.title}
                subtitle={tileWithPrompt.phoneSubtitle ?? tileWithPrompt.subtitle}
                prompt={tileWithPrompt.prompt}
                onClick={() => onTileClick?.(tileWithPrompt.prompt, tileWithPrompt.tool)}
                mode={mode}
                disableHoverAnimation={true}
                style={{
                  // Calculate width: 2 full tiles + 1/8 of third tile + gaps
                  // (100vw - padding - gaps) / 2.125
                  width: `calc((100vw - ${totalOffset}) / 2.125)`,
                  height: '100%',
                  minWidth: '140px',
                  flexShrink: 0,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}